import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, BarChart3, Bell, Calendar, LayoutGrid, Lock, Search, ShieldCheck, Star } from "lucide-react";

const PILLARS = [
  {
    label: "WORK",
    color: "#017bef",
    items: ["Coworking Spaces", "Dedicated Desks", "Meeting Rooms", "Conference Rooms", "Private Offices"],
  },
  { label: "STAY", color: "#0062c0", items: ["Hotels", "Apartments", "Boutique Hotels"] },
  { label: "PLAY", color: "#6366f1", items: ["Wellness", "Dining", "Adventure", "Events", "Concierge"] },
];

export function PlatformFoundation() {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center px-[16px] sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">The Foundation</p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Built Around Work. Stay. Play.
        </h2>

        <div className="w-full max-w-[768px] pt-14">
          <div className="grid grid-cols-1 gap-[20px] sm:grid-cols-3">
            {PILLARS.map(pillar => (
              <div key={pillar.label} className="rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white p-6">
                <span
                  className="inline-block rounded-[14px] px-[12px] py-1.5 text-[10px] font-bold leading-[15px] text-white"
                  style={{ backgroundColor: pillar.color }}
                >
                  {pillar.label}
                </span>
                <ul className="space-y-2 pt-[16px]">
                  {pillar.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-[14px] leading-5 text-[#717182]">
                      <span className="size-1 shrink-0 rounded-full" style={{ backgroundColor: pillar.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Connector down to the platform band */}
          <div aria-hidden className="flex flex-col items-center gap-1 pt-8">
            <span className="h-8 w-px bg-[#017bef]/25" />
            <span className="size-3 rounded-full border-2 border-solid border-[#017bef] bg-[#f5f8ff]" />
            <span className="h-8 w-px bg-[#017bef]/25" />
          </div>

          <div className="mt-6 rounded-3xl bg-[#012d6e] px-8 py-6 text-center drop-shadow-[0_8px_20px_rgba(1,45,110,0.2)]">
            <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white/50">
              Underlying Platform
            </p>
            <p className="pt-2 text-[24px] font-bold leading-8 tracking-[1.2px] text-white">ONE SPACIA PLATFORM</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Search, title: "Unified search and discovery" },
  { icon: Calendar, title: "Real-time availability" },
  { icon: ShieldCheck, title: "Secure online payments" },
  { icon: Star, title: "Verified listings and reviews" },
  { icon: LayoutGrid, title: "Booking management and history" },
  { icon: Bell, title: "Automated notifications and reminders" },
  { icon: BarChart3, title: "Partner dashboard" },
  { icon: BarChart3, title: "Reporting and performance analytics" },
  { icon: Lock, title: "Role-based access and enterprise security" },
];

export function PlatformFeatureGrid() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center px-[16px] sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          Platform Features
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          The SPACIA Platform
        </h2>

        <div className="grid w-full max-w-[1024px] grid-cols-1 gap-[16px] pt-14 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title }, index) => (
            <div
              key={title}
              className="flex items-center gap-[16px] rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] px-[20px] py-[16px]"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
                <Icon className="size-4 text-[#017bef]" />
              </span>
              <div>
                <p className="text-[10px] font-bold leading-[15px] tracking-[1px] text-[#717182]">
                  {String(index + 1).padStart(2, "0")}
                </p>
                <p className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PlatformCta() {
  const history = useHistory();

  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#012d6e] px-6 py-12 shadow-[0_8px_48px_0_rgba(1,45,110,0.2)] sm:px-12">
          <div className="pointer-events-none absolute left-1/3 top-0 h-[250px] w-[500px] rounded-full bg-[#017bef]/20 blur-[64px]" />

          <div className="relative flex flex-col items-center text-center">
            <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-white/50">
              One Platform. Every Space.
            </p>
            <h2 className="pt-[16px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-white sm:text-[48px] sm:leading-[48px]">
              Ready to Discover SPACIA?
            </h2>
            <p className="max-w-[576px] pt-[20px] text-[18px] leading-[29.25px] text-white/60">
              Wherever you need to Work, Stay or Play, SPACIA gives you one place to find it, book it and manage it with
              confidence.
            </p>
            <p className="max-w-[576px] pt-[20px] text-[16px] leading-[26px] text-white/40">
              SPACIA brings flexible workspaces, quality accommodation and curated experiences together so you can spend
              less time searching and more time doing what matters.
            </p>

            <div className="flex flex-wrap justify-center gap-[16px] pt-8">
              <button
                type="button"
                onClick={() => history.push("/package-signup")}
                className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-8 py-[16px] text-[16px] font-bold leading-6 text-white drop-shadow-[0_4px_12px_rgba(1,123,239,0.3)] transition-colors hover:bg-[#0062c0]"
              >
                Get Started
                <ArrowRight className="size-[18px]" />
              </button>
              <button
                type="button"
                onClick={() => history.push("/partner-signup")}
                className="rounded-full border-[1px] border-solid border-white/20 px-8 py-[16px] text-[16px] font-semibold leading-6 text-white transition-colors hover:bg-white/10"
              >
                List Your Space
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = "mailto:sales@spacia.africa";
                }}
                className="rounded-full border-[1px] border-solid border-white/20 px-8 py-[16px] text-[16px] font-semibold leading-6 text-white transition-colors hover:bg-white/10"
              >
                Speak to Our Team
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
